import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import RoomIcon from "@mui/icons-material/Room";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import RoomRestrictionBadge from "./RoomRestrictionBadge";
import DetailsNotesTabs from "./DetailsNotesTabs";
import {
  getStatusColor,
  getRoomNameColor,
  isHighPriority,
  isNonRoutineTask,
  isTerminalTaskStatus,
  toText,
} from "./servicesUtils";

// Technician (external) view. Deliberately standalone rather than sharing a
// base layout with InternalJobCard - the two are expected to diverge
// structurally over time, not just gain/lose an appended block.
function TechnicianJobCard({
  job,
  index,
  jobOrderToggle,
  jobOrderSelectedId,
  onJobOrderSelect,
  onOpenJob,
  t,
}) {
  const tasks = Array.isArray(job.tasks) ? job.tasks : [];
  const doneTasks = tasks.filter((task) =>
    isTerminalTaskStatus(task?.status),
  ).length;
  const hasNewTask = tasks.some((task) => isNonRoutineTask(task));
  const isJobCompleted = toText(job?.status) === "completed";
  const isPickedForOrder =
    jobOrderToggle &&
    !isJobCompleted &&
    jobOrderSelectedId === String(job.pk_id);
  const isOrderLocked = jobOrderToggle && isJobCompleted;

  return (
    <Card
      elevation={0}
      onClick={
        jobOrderToggle && !isJobCompleted
          ? () => onJobOrderSelect(job.pk_id)
          : undefined
      }
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: isPickedForOrder ? "primary.main" : "divider",
        boxShadow: isPickedForOrder
          ? (theme) => `0 0 0 2px ${theme.palette.primary.main}`
          : "none",
        cursor: jobOrderToggle && !isJobCompleted ? "pointer" : "default",
        opacity: isOrderLocked ? 0.6 : 1,
        transition: "border-color 40ms ease, box-shadow 40ms ease",
      }}
    >
      <CardContent sx={{ py: 1.35, px: 1.5 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.8} alignItems="center">
              {jobOrderToggle && !isJobCompleted ? (
                <DragIndicatorRoundedIcon
                  fontSize="small"
                  sx={{
                    color: isPickedForOrder ? "primary.main" : "text.disabled",
                  }}
                />
              ) : null}
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2.05rem", sm: "2.6rem" },
                  lineHeight: 1,
                  letterSpacing: "-0.02em",
                  color: getRoomNameColor(job?.room_restriction),
                }}
                noWrap
              >
                {index + 1}. {job.room_name}
              </Typography>
              {isHighPriority(job?.priority) ? (
                <Chip
                  size="small"
                  label={t("highPriority")}
                  color="error"
                  variant="outlined"
                  sx={{ height: 22, fontSize: "0.72rem" }}
                />
              ) : null}
            </Stack>
          </Box>
          <Chip
            size="medium"
            label={job.status || "unknown"}
            color={getStatusColor(job.status)}
            sx={{
              fontSize: "0.9rem",
              height: 34,
              textTransform: "lowercase",
            }}
          />
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mt: 1.35 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.9}>
            <RoomRestrictionBadge restriction={job?.room_restriction} />
            <Typography
              variant="subtitle1"
              color={hasNewTask ? "error.main" : "text.secondary"}
              sx={{
                fontSize: { xs: "1.9rem", sm: "2.45rem" },
                lineHeight: 1,
                fontWeight: hasNewTask ? 700 : 500,
              }}
            >
              {t("tasks")} {doneTasks}/{tasks.length}
            </Typography>
            {hasNewTask ? (
              <Chip
                size="small"
                label={t("new")}
                color="error"
                sx={{ height: 22, fontSize: "0.72rem" }}
              />
            ) : null}
          </Stack>
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          spacing={1}
          sx={{ mt: 0.8 }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            component="div"
            sx={{ fontSize: "1.25rem" }}
          >
            {job.job_number}
          </Typography>

          <Button
            size="large"
            variant="outlined"
            sx={{
              fontSize: { xs: "1.45rem", sm: "2.2rem" },
              px: { xs: 2.6, sm: 3.3 },
              py: { xs: 0.85, sm: 1 },
              minWidth: { xs: 138, sm: 168 },
              borderRadius: 1.8,
              letterSpacing: "0.02em",
            }}
            onClick={(event) => {
              if (jobOrderToggle) {
                // In reorder mode, clicks are for picking jobs
                // to swap, not opening the details drawer.
                event.stopPropagation();
                onJobOrderSelect(job.pk_id);
                return;
              }
              onOpenJob(job.pk_id);
            }}
            disabled={jobOrderToggle && isJobCompleted}
          >
            {jobOrderToggle ? t("jobOrdering.selectToSwap") : t("openButton")}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

// Internal (Admin/User) view. Kept fully independent of TechnicianJobCard so
// its layout/content can be reworked freely without touching the technician
// view - today it happens to look similar plus InternalJobMeta, but that's
// not an invariant to preserve.
function InternalJobCard({
  job,
  index,
  jobOrderToggle,
  jobOrderSelectedId,
  onJobOrderSelect,
  onOpenJob,
  t,
}) {
  const tasks = Array.isArray(job.tasks) ? job.tasks : [];
  const doneTasks = tasks.filter((task) =>
    isTerminalTaskStatus(task?.status),
  ).length;
  const hasNewTask = tasks.some((task) => isNonRoutineTask(task));
  const isJobCompleted = toText(job?.status) === "completed";
  const isPickedForOrder =
    jobOrderToggle &&
    !isJobCompleted &&
    jobOrderSelectedId === String(job.pk_id);
  const isOrderLocked = jobOrderToggle && isJobCompleted;

  return (
    <Card
      elevation={0}
      onClick={
        jobOrderToggle && !isJobCompleted
          ? () => onJobOrderSelect(job.pk_id)
          : undefined
      }
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: isPickedForOrder ? "primary.main" : "divider",
        boxShadow: isPickedForOrder
          ? (theme) => `0 0 0 2px ${theme.palette.primary.main}`
          : "none",
        cursor: jobOrderToggle && !isJobCompleted ? "pointer" : "default",
        opacity: isOrderLocked ? 0.6 : 1,
        transition: "border-color 40ms ease, box-shadow 40ms ease",
      }}
    >
      <CardContent sx={{ py: 1.35, px: 1.5 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.8} alignItems="center">
              {jobOrderToggle && !isJobCompleted ? (
                <DragIndicatorRoundedIcon
                  fontSize="small"
                  sx={{
                    color: isPickedForOrder ? "primary.main" : "text.disabled",
                  }}
                />
              ) : null}
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2rem", sm: "2rem" },
                }}
              >
                {index + 1}. {job.job_number}
              </Typography>

              {isHighPriority(job?.priority) ? (
                <Chip
                  size="small"
                  label={t("highPriority")}
                  color="error"
                  variant="outlined"
                  sx={{ height: 22, fontSize: "0.72rem" }}
                />
              ) : null}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AccountBoxIcon fontSize="small" color="action" />
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ fontSize: { xs: "1.3rem", sm: "1.3rem" } }}
              >
                {job.customer_name || "unknown"}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{ fontSize: { xs: "1.3rem", sm: "1.3rem" } }}
              >
                <RoomIcon fontSize="small" color="action" />
                {job.location_name || "unknown"}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.3rem", sm: "1.3rem" },
                  lineHeight: 1,
                  letterSpacing: "-0.02em",
                  color: getRoomNameColor(job?.room_restriction),
                }}
                noWrap
              >
                {job.device_name ? (
                  <SmartToyOutlinedIcon fontSize="small" color="action" />
                ) : (
                  <MeetingRoomIcon fontSize="small" color="action" />
                )}
                {job.device_name || job.room_name}
              </Typography>
            </Stack>
          </Box>
          <Chip
            size="medium"
            label={job.status || "unknown"}
            color={getStatusColor(job.status)}
            sx={{
              fontSize: "0.9rem",
              height: 34,
              textTransform: "lowercase",
            }}
          />
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          sx={{ mt: 1.35 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.9}>
            <RoomRestrictionBadge restriction={job?.room_restriction} />
            <Typography
              variant="subtitle1"
              color={hasNewTask ? "error.main" : "text.secondary"}
              sx={{
                fontSize: { xs: "1.9rem", sm: "2.45rem" },
                lineHeight: 1,
                fontWeight: hasNewTask ? 700 : 500,
              }}
            >
              {t("tasks")} {doneTasks}/{tasks.length}
            </Typography>
            {hasNewTask ? (
              <Chip
                size="small"
                label={t("new")}
                color="error"
                sx={{ height: 22, fontSize: "0.72rem" }}
              />
            ) : null}
          </Stack>

          <Button
            size="large"
            variant="outlined"
            sx={{
              fontSize: { xs: "1.45rem", sm: "2.2rem" },
              px: { xs: 2.6, sm: 3.3 },
              py: { xs: 0.85, sm: 1 },
              minWidth: { xs: 138, sm: 168 },
              borderRadius: 1.8,
              letterSpacing: "0.02em",
            }}
            onClick={(event) => {
              if (jobOrderToggle) {
                // In reorder mode, clicks are for picking jobs
                // to swap, not opening the details drawer.
                event.stopPropagation();
                onJobOrderSelect(job.pk_id);
                return;
              }
              onOpenJob(job.pk_id);
            }}
            disabled={jobOrderToggle && isJobCompleted}
          >
            {jobOrderToggle ? t("jobOrdering.selectToSwap") : t("openButton")}
          </Button>
        </Stack>

        <Box sx={{ mt: 0.5 }}>
          <DetailsNotesTabs job={job} dense />
        </Box>
      </CardContent>
    </Card>
  );
}

// Presentational job list for the My Services day tab. All state/business
// logic (ordering, fetching, selection) lives in the parent page; this
// component only renders cards and reports taps back via callbacks.
export default function ServicesJobList({
  jobs,
  jobOrderToggle,
  jobOrderSelectedId,
  onJobOrderSelect,
  onOpenJob,
  viewMode = "technician",
  t,
}) {
  const isInternalView = viewMode === "internal";

  if (jobs.length === 0) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <CardContent sx={{ py: 1.5 }}>
          <Typography variant="body1" color="text.secondary">
            {t("noJobsForDay")}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const JobCard = isInternalView ? InternalJobCard : TechnicianJobCard;

  return (
    <Stack spacing={1}>
      {jobs.map((job, index) => (
        <JobCard
          key={job.pk_id}
          job={job}
          index={index}
          jobOrderToggle={jobOrderToggle}
          jobOrderSelectedId={jobOrderSelectedId}
          onJobOrderSelect={onJobOrderSelect}
          onOpenJob={onOpenJob}
          t={t}
        />
      ))}
    </Stack>
  );
}
